import type { TFunction } from 'i18next';
import type { Alarm, MissionEvent } from '@sentinel/shared';

/** Chronicle lines and alarms are stored as message keys + params: the text is produced here,
 *  in the user's language, never concatenated by hand. */
export function eventText(t: TFunction, event: MissionEvent): string {
  const key = `chronicle.events.${event.messageKey}`;
  const params = { ...event.messageParams, ...levelParam(t, event.messageParams) };
  return t(key, {
    ...params,
    defaultValue: t('chronicle.events.unknown', { key: event.messageKey }),
  });
}

export function alarmText(t: TFunction, alarm: Alarm): string {
  const key = `chronicle.alarms.${alarm.messageKey}`;
  return t(key, {
    ...alarm.messageParams,
    defaultValue: t('chronicle.alarms.unknown', { key: alarm.messageKey }),
  });
}

/** Alert levels inside params are enum values: translate them to the local word. */
function levelParam(t: TFunction, params: Record<string, string | number>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const name of ['level', 'from', 'to']) {
    const value = params[name];
    if (typeof value === 'string' && ['green', 'yellow', 'orange', 'red'].includes(value))
      out[name] = t(`common.level.short.${value}`);
  }
  return out;
}

export const EVENT_COLOUR: Record<string, string> = {
  alarm: 'var(--crit)',
  assignment: 'var(--gold-ink)',
  media: 'var(--gold-ink)',
  event: 'var(--gold-ink)',
  status: 'var(--ink-3)',
  communication: 'var(--ink-3)',
  position: 'var(--ink-3)',
  resource: 'var(--ink-3)',
  note: 'var(--sel)',
};

export function eventColour(event: MissionEvent): string {
  if (event.kind === 'alarm' && (event.level ?? 0) < 4) return 'var(--att)';
  if (event.messageKey === 'sector_completed') return 'var(--ok)';
  return EVENT_COLOUR[event.kind] ?? 'var(--ink-3)';
}
